/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {Box, Divider, Flex, themeContext} from 'exsportia-components'
import {useFormikContext} from 'formik'
import {DateTime} from 'luxon'
import {FC, memo, useCallback, useContext, useEffect, useMemo} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'

import {isValidPromoCode, normalizePromoCode} from '../../api/promo-codes/helpers'
import {useSubscription} from '../../api/subscriptions/hooks'
import {getPassPromoPricing} from '../../api/subscriptions/promo'
import {useVenue} from '../../api/venue/hooks'
import {CheckoutGateNotice} from '../../components/checkout-gate-notice/checkout-gate-notice'
import {EmptyList} from '../../components/empty-list/empty-list'
import {ExsportiaPayIcon, PaymentMethodsGroup} from '../../components/payment-icons'
import {useSubmitAfterRender} from '../../hooks/use-submit-after-render'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {SimpleGridLayoutHeader} from '../../layout/grid-layout/headers/simple-grid-layout-header'
import {useHandleLoginState} from '../../services/auth/hooks'
import {useOnlinePayability} from '../../services/payments/hooks/use-online-payability'
import {PaymentSubmit, PaymentTypes, usePaymentOptions} from '../../services/payments/hooks/use-payment-options'
import {SubscriptionFormType} from '../../services/subscription/forms/subscription-purchase-form'
import {useUser} from '../../services/user/hooks/use-user'
import {ThemeType} from '../../theme/types'
import {Button} from '../../ui/components/button/button'
import {RadioButton} from '../../ui/components/radio-button'
import {useLocale, useLuxon} from '../../utils/i18n'
import {isNotNilAndNotEmpty} from '../../utils/logic'
import {UserPaymentsMethods} from '../set-payment-method/components/user-payments-type-modal'
import {CycleOption, subscriptionPlansToSelectOptions} from './helpers'
import {SubscriptionSummarySection} from './payment/subscription-summary-section'

type CyclesByTypeMap = {[key in CycleOption['type']]: CycleOption}

// TODO: move to separated component file
type Props = {
  paymentType: PaymentTypes
  isLoading: boolean
  isFreeSubscription: boolean
  onClick: () => void
  disabled?: boolean
}
const SubmitButton: FC<Props> = memo(({paymentType, isLoading, onClick, isFreeSubscription, disabled = false}) => {
  const {l} = useLocale()

  let text =
    paymentType === PaymentTypes.PAY_ON_LOCATION
      ? l('book')
      : paymentType === PaymentTypes.ONE_TIME
      ? l('continue')
      : l('pay')

  if (isFreeSubscription) {
    text = l('get_subscription', {caseAction: 'capitalize'})
  }

  return (
    <Button
      disabled={isLoading || disabled}
      onClick={onClick}
      isLoading={isLoading}
    >
      {text}
    </Button>
  )
})

const Header: FC = memo(() => {
  const {l} = useLocale()
  return <SimpleGridLayoutHeader title={l('paymentMethod')} />
})

// eslint-disable-next-line
export const SubscriptionPaymentPage: FC = () => {
  const {l} = useLocale()
  const luxon = useLuxon()
  const {theme} = useContext(themeContext) as {theme: ThemeType}
  const navigate = useNavigate()
  const {data: venue, isFetching: isVenueLoading, refetch: refetchVenue} = useVenue()
  const [searchParams] = useSearchParams()
  const {submitForm, setValues, values, setFieldValue, isSubmitting} = useFormikContext<SubscriptionFormType>()
  const {data: subscription, isFetching: isSubscriptionLoading, refetch: refetchSubscription} = useSubscription(
    undefined,
    {promoCode: values.promoCode || undefined, firstCyclePrice: values.promoFirstCyclePrice ?? undefined},
  )
  const {data: user} = useUser()

  useEffect(() => {
    const promoFromUrl = searchParams.get('promoCode')
    if (promoFromUrl && typeof promoFromUrl === 'string') {
      const upper = normalizePromoCode(promoFromUrl)
      const isValid = isValidPromoCode(upper)
      if (isValid && values.promoCode !== upper) {
        setFieldValue('promoCode', upper).catch((e) => console.log(String(e)))
      }
    }
  }, [])

  const cyclesConfig = useMemo(() => {
    if (isNotNilAndNotEmpty(subscription) && venue?.currency) {
      return subscriptionPlansToSelectOptions({
        currency: venue.currency,
        subscription,
        startDate: values.selectedStartDate ? luxon
        .fromObject(
          {
            year: luxon.fromMillis(values.selectedStartDate).year,
            month: luxon.fromMillis(values.selectedStartDate).month,
            day: luxon.fromMillis(values.selectedStartDate).day,
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
          },
          {zone: 'utc'},
        )
        .toMillis() : null,
        timeZone: venue.timeZone,
      })
    }
    return []
  }, [subscription, venue, values.selectedStartDate])

  const cyclesMapByType = useMemo(
    () =>
      cyclesConfig.reduce(
        (acc, cycle) => ({
          ...acc,
          [cycle.type]: cycle,
        }),
        // eslint-disable-next-line @typescript-eslint/prefer-reduce-type-parameter
        {} as CyclesByTypeMap,
      ),
    [cyclesConfig],
  )

  const selectedOption = useMemo(
    () => (values.plan?.type !== undefined ? cyclesMapByType[values.plan.type] : null),
    [values],
  )

  const firstCyclePrice = selectedOption?.cycles[0]?.price
  useEffect(() => {
    if (firstCyclePrice !== undefined && firstCyclePrice !== values.promoFirstCyclePrice) {
      setFieldValue('promoFirstCyclePrice', firstCyclePrice).catch((e) => console.log(String(e)))
    }
  }, [firstCyclePrice])

  const promoPricing = useMemo(() => {
    const promo = subscription?.promoApplied
    if (!selectedOption || !promo || promo.code !== values.promoCode) {
      return null
    }
    return getPassPromoPricing({
      cycles: selectedOption.cycles,
      plan: selectedOption.plan,
      promo,
      listTotal: selectedOption.totalPrice,
    })
  }, [selectedOption, subscription?.promoApplied, values.promoCode])

  const subscriptionDraft = useMemo(() => {
    const plan = values.plan
    const cycles = selectedOption?.cycles
    if (!user || !venue || !plan || !subscription || !cycles || cycles.length === 0) {
      return null
    }
    const endDates: number[] = []
    const currentDate = DateTime.now().toMillis()
    const lastPaymentDate = Math.max(...endDates, endDates.length)
    const checkActiveStatus = lastPaymentDate > currentDate
    const startDate = cycles[0].startDate
    const endDate = cycles[cycles.length - 1].endDate
    const mapCycles = cycles.map(({diffDaysForPayAccordingNow: _, disabled: __, isFullMonth: ___, ...rest}) => ({
      ...rest,
    }))
    let subscriptionDraftTemp: NonNullable<SubscriptionFormType['subscriptionDraft']> = {
      endDate,
      currency: venue.currency,
      startDate,
      checkActiveStatus,
      plan,
      cycles: mapCycles,
      userId: user.uid,
      // List price: with a code the server sets the real defaultCyclePrice (#2835).
      defaultCyclePrice: plan.listPrice ?? plan.price,
      venueId: subscription.venueId,
      membershipId: subscription.uid,
      systemType: 'BOOKING_WEB',
      priceTime: plan.type,
      userName: user.name || 'User name',
      promoCode: values.promoCode || null,
    }

    if (plan.additionalFee) {
      subscriptionDraftTemp = {
        ...subscriptionDraftTemp,
        isPaidAdditionalFee: true,
        additionalFee: plan.additionalFee,
      }
    }

    return subscriptionDraftTemp
  }, [selectedOption, values.plan, user, venue, subscription])

  useEffect(() => {
    if (subscriptionDraft && selectedOption) {
      setFieldValue('subscriptionDraft', subscriptionDraft).catch((e) => console.log(String(e)))
      // With a code, `sum` / `totalPrice` is the discounted pay-today amount; cycles stay at list.
      setFieldValue('totalPrice', promoPricing ? promoPricing.payToday : selectedOption.totalPrice)
        .catch((e) => console.log(String(e)))
    }
  }, [subscriptionDraft, promoPricing])

  useEffect(() => {
    if (subscription) {
      setFieldValue('subscription', subscription).catch((e) => console.log(String(e)))
    }
  }, [subscription])

  // The pay handler can run after an await (Opn charge-status read): submit only once the values
  // set below are rendered, or the first click submits the old payment type and does nothing.
  const submitAfterRender = useSubmitAfterRender(submitForm)
  const handleSubmitWithPayment = useCallback(
    (paymentMethodToken: string | null) => {
      setValues({...values, paymentMethodToken, paymentType: PaymentTypes.PAY_NOW})
      submitAfterRender()
    },
    [values, setValues, submitAfterRender],
  )
  const handleClickButton: PaymentSubmit = useCallback(
    ({paymentType, paymentMethodToken}) => {
      // const run = async () => {
        // try {
          // const [subscriptionResult] = await Promise.all([refetchSubscription(), refetchVenue()])
          // const updatedSubscription = subscriptionResult.data
          // if (!updatedSubscription) {
          //   showMessage(l('somethingWentWrong'), 'error')
          //   navigate('../')
          //   return
          // }
          // const updatedIsValidated = updatedSubscription.permissionData.validated
          // const updatedNeedRequest =
          //   updatedSubscription.byRequestOnly && updatedSubscription.requestStatus !== 'ACCEPT'
          // if (!updatedIsValidated || updatedNeedRequest) {
          //   showMessage(l('permissions_were_changed', {caseAction: 'capitalize'}), 'error')
          //   navigate('../')
          //   return
          // }
        // } catch {
          // showMessage(l('permissions_were_changed', {caseAction: 'capitalize'}), 'error')
          // navigate('../')
          // return
        // }

        if (paymentType === PaymentTypes.PAY_NOW || values.totalPrice === 0) {
          handleSubmitWithPayment(paymentMethodToken)
          return
        }
        if (paymentType === PaymentTypes.ONE_TIME) {
          setValues({
            ...values,
            paymentType: PaymentTypes.ONE_TIME,
            paymentMethodToken: null,
          })
          submitAfterRender()
          return
        }
      // }
      // void run()
    },
    [
      refetchSubscription,
      refetchVenue,
      l,
      navigate,
      handleSubmitWithPayment,
      setValues,
      values,
      submitAfterRender,
    ],
  )
  const {
    isLoading: isPaymentsLoading,
    paymentType,
    allowedOptions,
    setPaymentType,
    submitWithPayment,
    paymentMethodToken,
    setUserPaymentToken,
    checkoutGate,
    isCheckingCharge,
  } = usePaymentOptions({
    submit: handleClickButton,
    // @ts-ignore
    entity: subscription, // TODO: fix type
    entityType: 'subscription',
    totalPrice: values.totalPrice,
  })
  const isLoading = isVenueLoading || isSubscriptionLoading || isPaymentsLoading || isSubmitting
    || Boolean(promoPricing && !promoPricing.ready)

  // Reached by a deep link or the back button, since the profile page now disables the CTA. Without
  // a terminal there are no options to offer, and the generic "by request only" copy blamed the
  // wrong thing — say what is actually true.
  const {hasNoPaymentTerminal} = useOnlinePayability()

  const handleLogOut = useCallback(() => {
    navigate('../', {replace: true})
  }, [values])

  useHandleLoginState({
    handleLogOut,
    handleLoggedOut: handleLogOut,
  })

  const navigateBackToProfile = useCallback(() => {
    navigate('../')
  }, [])
  const setOneTimePayment = useCallback(() => setPaymentType(PaymentTypes.ONE_TIME), [])
  // const setPayOnLocation = useCallback(() => setPaymentType(PaymentTypes.PAY_ON_LOCATION), [])

  return (
    <GridLayout header={<Header />}>
      {
        (allowedOptions.length === 0 && !isPaymentsLoading && !isSubscriptionLoading) ? (
        <EmptyList
          onClick={navigateBackToProfile}
          text={hasNoPaymentTerminal ? l('onlinePurchaseUnavailable') : l('byRequestOnly')}
          description={hasNoPaymentTerminal ? l('onlinePurchaseUnavailableDesc') : undefined}
          buttonText={l('back')}
        />
      ) : (
        <Box>
          <CheckoutGateNotice gate={checkoutGate} />
          {values.totalPrice != 0 && (
            <Box mb='16px'>
              <Divider />
              {allowedOptions.includes(PaymentTypes.ONE_TIME) && (
                <>
                  <Flex
                    alignItems='center'
                    height={(theme.spaces.multiplier as number) * 6}
                  >
                    <RadioButton
                      disabled={isLoading || checkoutGate.isGated}
                      checked={paymentType === PaymentTypes.ONE_TIME}
                      onChange={setOneTimePayment}
                      text={l('exsportiaPay', {caseAction: 'capitalize'})}
                      iconBefore={<ExsportiaPayIcon w={40} h={24} />}
                      iconAfter={<Box ml='auto'><PaymentMethodsGroup w={180} h={32} /></Box>}
                    />
                  </Flex>
                  <Divider />
                </>
              )}
              {allowedOptions.includes(PaymentTypes.PAY_NOW) && (
                <>
                  <Box>
                    <UserPaymentsMethods
                      isLoading={isLoading || checkoutGate.isGated}
                      venueId={venue?.uid}
                      paymentMethod={paymentMethodToken}
                      setPaymentMethod={setUserPaymentToken}
                    />
                  </Box>
                </>
              )}
            </Box>
          )}
          <SubmitButton
            isFreeSubscription={values.totalPrice === 0}
            paymentType={paymentType}
            isLoading={isLoading || isCheckingCharge}
            onClick={() => submitWithPayment()}
            disabled={checkoutGate.isSelectionBlocked}
          />
        </Box>
      )}
      <Box>
        <SubscriptionSummarySection cyclesConfig={selectedOption} />
      </Box>
    </GridLayout>
  )
}
